import { useFileUpload } from "@msuite/ui/file-upload";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type TicketFormSchema, ticketSchema } from "@msuite/utils/schemas";
import type { UploadResult } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { ProjectSearch } from "@/components/projects";
import { TicketForm } from "@/forms/ticket-form";
import { client } from "@/hono";
import { useExistingFileController } from "@/hooks/use-existing-file-controller";
import { useResourceLinkController } from "@/hooks/use-resource-link-controller";

/** Props Interface */
interface AddTicketModalProps extends ModalProps {
	projectId: string | null;
	onComplete?: (ticket: TicketFormSchema, uploadResults: UploadResult[]) => Promise<void> | void;
	withProjectSearch?: boolean;
}

export function AddTicketModal({
	projectId,
	onComplete,
	withProjectSearch = true,
	...props
}: AddTicketModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		defaultValues: {
			state: "active",
			projectId: projectId || "NULL",
			dateMode: "date",
		},
		resolver: resolver(ticketSchema),
	});
	const { uploadFn } = useUpload();
	const controller = useFileUpload({
		uploadFn,
	});
	const resourceLinkController = useResourceLinkController();
	const existingFileController = useExistingFileController();
	const qc = useQueryClient();

	/** Functions */
	async function handleOnSubmit(json: TicketFormSchema) {
		try {
			const uploadResults = await controller.handleOnFileUpload();
			if (json.projectId === "NULL") {
				onComplete?.(json, uploadResults);
				props.onClose();
				return;
			}
			const response = await client.tickets.index.$post({
				json: {
					...json,
					uploadResults,
				},
			});
			const data = await responseHandler(response);
			if (data.ticket) {
				await resourceLinkController.handleOnPersistPendingLinks(json.projectId, data.ticket.id);
				if (existingFileController.pendingFileIds.length > 0) {
					const filesResponse = await client.tickets[":ticketId"].files.$post({
						param: { ticketId: data.ticket.id },
						json: { existingFileIds: existingFileController.pendingFileIds },
					});
					await responseHandler(filesResponse);
					existingFileController.handleOnReset();
				}
			}
			controller.handleOnReset();
			await qc.invalidateQueries({
				predicate: invalidate([json.projectId]),
			});
			props.onClose();
		} catch (error) {
			console.error(error);
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={props.isOpen}
				onClose={props.onClose}
				title={t("projects.add_ticket")}
				onSubmit={methods.handleSubmit(handleOnSubmit, (error) => {
					toast.error(JSON.stringify(error));
				})}
				data-cy="ticket-form"
			>
				<Modal.Body>
					{!projectId && withProjectSearch && (
						<ProjectSearch
							onChange={(projectId) => {
								if (!projectId) return null;
								methods.setValue("projectId", projectId);
							}}
						/>
					)}
					<TicketForm
						controller={controller}
						resourceLinkController={resourceLinkController}
						existingFileController={existingFileController}
					/>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
